import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import type { PickupLocation } from "../transporter/transporter";
import { Prospect, ProspectAssignedRepId } from "../leads/prospects";

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (typeof timestamp === "string") return new Date(timestamp);
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const cleanPickupLocation = (raw: Partial<PickupLocation> | undefined): PickupLocation | undefined => {
  if (!raw) return undefined;
  const address = raw.address?.trim() ?? "";
  const city = raw.city?.trim() ?? "";
  const state = raw.state?.trim() ?? "";
  const zip = raw.zip?.trim() ?? "";
  const contact_name = raw.contact_name?.trim();
  const contact_phone = raw.contact_phone?.trim();
  const notes = raw.notes?.trim();
  const company_name = raw.company_name?.trim();
  const secondary_contact_name = raw.secondary_contact_name?.trim();
  const secondary_contact_phone = raw.secondary_contact_phone?.trim();
  if (!address && !city && !state && !zip && !contact_name && !contact_phone && !notes) {
    return undefined;
  }
  const out: PickupLocation = {
    address,
    city,
    state,
    zip,
  };
  if (contact_name) out.contact_name = contact_name;
  if (contact_phone) out.contact_phone = contact_phone;
  if (notes) out.notes = notes;
  if (company_name) out.company_name = company_name;
  if (secondary_contact_name) out.secondary_contact_name = secondary_contact_name;
  if (secondary_contact_phone) out.secondary_contact_phone = secondary_contact_phone;
  return out;
};

const parseAssignedRepId = (v: unknown): ProspectAssignedRepId => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") return v;
  return null;
};

export const prospectConverter: FirestoreDataConverter<Prospect> = {
  toFirestore(prospect: Prospect) {
    const { id: _id, ...rest } = prospect;
    const dropoffLocation = cleanPickupLocation(rest.dropoffLocation);
    return {
      name: rest.name ?? "",
      ...(rest.phone?.trim() ? { phone: rest.phone.trim() } : {}),
      ...(rest.email?.trim() ? { email: rest.email.trim() } : {}),
      ...(rest.company?.trim() ? { company: rest.company.trim() } : {}),
      assignedRepId: rest.assignedRepId === undefined ? null : rest.assignedRepId,
      ...(dropoffLocation ? { dropoffLocation } : {}),
      createdAt: serializeDate(rest.createdAt),
      updatedAt: serializeDate(rest.updatedAt),
      createdByUserId: rest.createdByUserId ?? "",
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): Prospect {
    const data = snapshot.data(options);
    const rawDrop = data?.dropoffLocation as Partial<PickupLocation> | undefined;
    const dropoffLocation = cleanPickupLocation(rawDrop);

    const companyRaw = typeof data?.company === "string" ? data.company.trim() : "";
    const emailRaw = typeof data?.email === "string" ? data.email.trim() : "";
    const phoneRaw = typeof data?.phone === "string" ? data.phone.trim() : "";

    return {
      id: snapshot.id,
      name: typeof data?.name === "string" ? data.name : "",
      ...(phoneRaw ? { phone: phoneRaw } : {}),
      ...(emailRaw ? { email: emailRaw } : {}),
      ...(companyRaw ? { company: companyRaw } : {}),
      assignedRepId: parseAssignedRepId(data?.assignedRepId),
      ...(dropoffLocation ? { dropoffLocation } : {}),
      createdAt: convertTimestamp(data?.createdAt) || new Date(),
      updatedAt: convertTimestamp(data?.updatedAt) || new Date(),
      createdByUserId: typeof data?.createdByUserId === "string" ? data.createdByUserId : "",
    };
  },
};
